/**
 * A status tag: a small mark plus words, no box. Tones keep their old names and map to five meanings:
 * on (teal dot), open (hollow ring), off (grey dash), done (teal check), info ("i"), problem (brick triangle).
 * `failed` is kept as an alias of `problem`; use it only for something that goes wrong if ignored.
 */
export type PillTone =
  | 'active'
  | 'inactive'
  | 'healthy'
  | 'failed'
  | 'running'
  | 'planned'
  | 'linked'
  | 'notLinked'
  | 'done'
  | 'info'
  | 'problem';

type Mark = 'on' | 'open' | 'off' | 'done' | 'info' | 'problem';

const MARKS: Record<PillTone, Mark> = {
  active: 'on',
  healthy: 'on',
  linked: 'on',
  running: 'open',
  planned: 'open',
  inactive: 'off',
  notLinked: 'off',
  done: 'done',
  info: 'info',
  failed: 'problem',
  problem: 'problem',
};

export function StatusMark({ mark }: { mark: Mark }) {
  switch (mark) {
    case 'on':
      return <i aria-hidden className="inline-block flex-none w-2 h-2 rounded-full bg-accent" />;
    case 'open':
      return (
        <i aria-hidden className="inline-block flex-none w-2 h-2 rounded-full border-[1.5px] border-line-strong" />
      );
    case 'off':
      return <i aria-hidden className="inline-block flex-none w-[9px] h-[2px] bg-line-strong" />;
    case 'done':
      return (
        <svg aria-hidden width="12" height="12" viewBox="0 0 12 12" className="flex-none text-accent">
          <path d="M2 6.5l2.5 2.5L10 3.5" fill="none" stroke="currentColor" strokeWidth="2" />
        </svg>
      );
    case 'info':
      return (
        <svg aria-hidden width="14" height="14" viewBox="0 0 14 14" className="flex-none">
          <circle cx="7" cy="7" r="6" fill="none" stroke="currentColor" strokeWidth="1.2" />
          <path d="M7 6v4M7 3.6v.8" stroke="currentColor" strokeWidth="1.4" />
        </svg>
      );
    default:
      return <WarningIcon />;
  }
}

export function WarningIcon({ size = 14 }: { size?: number }) {
  return (
    <svg aria-hidden width={size} height={size} viewBox="0 0 14 14" className="flex-none">
      <path d="M7 1.2L13 12.5H1z" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M7 5.2v3.6M7 9.9v1" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

export default function Pill({
  tone,
  children,
  small = false,
}: {
  tone: PillTone;
  children: string;
  /** 13px, for dense cells. */
  small?: boolean;
}) {
  const mark = MARKS[tone];
  const colour = mark === 'problem' ? 'text-warning font-semibold' : mark === 'on' ? 'text-ink' : 'text-ink-2';
  return (
    <span
      className={`inline-flex items-center gap-[6px] whitespace-nowrap ${
        small ? 'text-small' : 'text-[15px] leading-[22px]'
      } ${colour}`}
    >
      <StatusMark mark={mark} />
      {children}
    </span>
  );
}

export function PhaseChip({ label = 'Phase 3', dashed = true }: { label?: string; dashed?: boolean }) {
  return (
    <span
      className={`inline-block rounded-sm border text-small leading-none px-[5px] py-[3px] ${'text-ink-3 border-line'} ${dashed ? 'border-dashed' : ''}`}
    >
      {label}
    </span>
  );
}
